import { Big } from 'big.js';
import {
    Article,
    bigToString,
    Corridor,
    CorridorLevel,
    EnumCorridorColor,
    EnumQuoteServiceSalesType,
    EnumQuoteServiceType,
    ProductPricing,
    Tools,
    parseBig
} from 'quote-domain-core';
import { CorridorService } from '../corridor/corridor-service';
import { singleton } from 'tsyringe';

@singleton()
export class ArticleService {
    constructor(private readonly corridorService: CorridorService) {
    }

    public updateTotalQuantity(article: Article, totalQuantity?: number) {
        Object.assign(article, { totalQuantity });
    }

    public updateCorridor(article: Article, userInputsForCorridorSearch: any, code: string, isCmc: boolean, salesType: EnumQuoteServiceSalesType, consoType?: string): void {
        const corridor = this.search(article, article.productPricings, userInputsForCorridorSearch, this.getArgumentsToMatch(article, code, isCmc, salesType));
        if (corridor) {
            Object.assign(article, { lastCorridorState: corridor });
            if (article.isRenewal && !article.appliedPrice) {
                Object.assign(article, { appliedPrice: article.oldAppliedPrice });
            }
        } else {
            if (article.isRenewal && (userInputsForCorridorSearch.quantity > 0 || consoType == 'VARIABLE')) {
                Object.assign(article, {
                    lastCorridorState: this.getNeutralCorridor(article, userInputsForCorridorSearch, consoType),
                    appliedPrice: !article.appliedPrice ? article.oldAppliedPrice : article.appliedPrice,
                });
            } else {
                Object.assign(article, {
                    corridor: null,
                    lastCorridorState: null,
                    appliedPrice: null,
                    appliedPriceColor: EnumCorridorColor.GRAY
                });
            }
        }
    }

    public updateCorridorFromSingleProductPricing(article: Article): void {
        const pricedProductPricing = article.productPricings?.find(productPricing => productPricing?.p90 != null);
        if (pricedProductPricing) {
            Object.assign(article, { lastCorridorState: this.mapProductPricingToCorridor(pricedProductPricing) });
        } else {
            Object.assign(article, {
                corridor: null,
                lastCorridorState: null,
                appliedPrice: null,
                appliedPriceColor: EnumCorridorColor.GRAY
            });
        }
    }

    private getArgumentsToMatch(article: Article, quoteServiceCode: string, isCmc: boolean, salesType: EnumQuoteServiceSalesType): string[] {
        const isExistingOrRenewal = salesType !== EnumQuoteServiceSalesType.NEW;
        const isFonOrCaf = quoteServiceCode === EnumQuoteServiceType.FON || quoteServiceCode === EnumQuoteServiceType.CAF;
        const isFonOrCafExistingOrRenewal = isExistingOrRenewal && isFonOrCaf;
        return isCmc ? this.getCmcCriteria(article, isFonOrCafExistingOrRenewal, quoteServiceCode, isExistingOrRenewal)
            : this.getStandardCriteria(isFonOrCafExistingOrRenewal, quoteServiceCode, isExistingOrRenewal);
    }

    private getStandardCriteria(isFonOrCafExistingOrRenewal: boolean, quoteServiceCode: string, isExistingOrRenewal: boolean) {
        if (isFonOrCafExistingOrRenewal) {
            return ['quantity', 'contractDuration', 'billingMode'];
        }
        switch (quoteServiceCode) {
            case EnumQuoteServiceType.SOL:
            case EnumQuoteServiceType.DASRI:
                return ['quantity', 'contractDuration', 'frequency', 'billingMode'];
            case EnumQuoteServiceType.VT:
                return ['quantity', 'contractDuration', 'frequency', 'billingMode', 'numberOfChange'];
            case EnumQuoteServiceType.LS:
                return ['quantity', 'contractDuration', 'frequency', 'billingMode'].concat(isExistingOrRenewal ? [] : ['exchangeRule']);
            default:
                return ['quantity', 'contractDuration', 'frequency', 'billingMode'];
        }
    }

    private getCmcCriteria(article: Article, isFonOrCafExistingOrRenewal: boolean, quoteServiceCode: string, isExistingOrRenewal: boolean) {
        if (isFonOrCafExistingOrRenewal) return ['quantity', 'billingMode'];
        switch (quoteServiceCode) {
            case EnumQuoteServiceType.VT:
                return ['quantity', 'frequency', 'billingMode', 'numberOfChange'].concat(article.isCmcWithStandardProductPricing ? [] : ['stockVT']);
            case EnumQuoteServiceType.LS:
                return ['quantity', 'frequency', 'billingMode'].concat(isExistingOrRenewal ? [] : ['exchangeRule']);
            default:
                return ['quantity', 'frequency', 'billingMode'];
        }
    }

    private search(article: Article, productPricings: any, userInputs: any, searchArguments: any): Corridor {
        const pp = productPricings?.filter((productPricing: any) => {
            if (!productPricing) {
                return false;
            }
            let isMatch = true;
            searchArguments.forEach((searchArgument: any) => {
                if (searchArgument === 'quantity') {
                    if (!this.isQuantityMatching(productPricing, userInputs['quantity'])) {
                        isMatch = false;
                    }
                } else {
                    const fieldValue = productPricing.productPricingDetails[searchArgument];
                    const matchValue = this.isObject(userInputs[searchArgument])
                        ? userInputs[searchArgument].key : userInputs[searchArgument];
                    const isFieldMatching = Array.isArray(fieldValue) ? fieldValue.includes(matchValue) : fieldValue === matchValue;
                    if (!isFieldMatching) {
                        isMatch = false;
                    }
                }
            });
            return isMatch;
        });
        if (pp?.length > 0) {
            return this.mapProductPricingToCorridor(pp[0]);
        }
        return null;
    }

    private isQuantityMatching(productPricing: ProductPricing, quantity: number): boolean {
        return (quantity >= Number(productPricing.productPricingDetails.quantityMin))
            && quantity <= (Number(productPricing.productPricingDetails.quantityMax));
    }

    private mapProductPricingToCorridor(productPricing: ProductPricing): Corridor {
        const levels = new Map<EnumCorridorColor, CorridorLevel>();
        levels.set(EnumCorridorColor.GREEN, new CorridorLevel(new Big(productPricing.p90).eq(0) ? "0" : "10000", productPricing.p90.toString()));
        levels.set(EnumCorridorColor.LIGHTGREEN, new CorridorLevel(productPricing.p90.toString(), productPricing.p50p90.toString()));
        levels.set(EnumCorridorColor.ORANGE, new CorridorLevel(productPricing.p50p90.toString(), productPricing.p50.toString()));
        levels.set(EnumCorridorColor.RED, new CorridorLevel(productPricing.p50.toString(), productPricing.p10p50.toString()));
        levels.set(EnumCorridorColor.BLACK, new CorridorLevel(productPricing.p10p50.toString(), productPricing.p10.toString()));
        levels.set(EnumCorridorColor.BELOW_BLACK, new CorridorLevel(productPricing.p10.toString(), "0"));
        return Object.assign(new Corridor(), { levels: levels });
    }

    private isObject(val: any) {
        if (val === null) {
            return false;
        }
        return ((typeof val === 'function') || (typeof val === 'object'));
    }

    public updateLastCorridorStateByAddingPrice(article: Article, logisticsPriceImpact: Big): void {
        if (article.lastCorridorState) {
            this.corridorService.addingPrice(article.lastCorridorState, logisticsPriceImpact);
        }
    }

    public calculateUnitaryPrice(article: Article, frequency: any, referential: any): Big {
        if (frequency) {
            const visitingFrequencyInputValue = referential.frequencies.filter((freq: any) => freq.frequency === frequency.value)[0];
            if (visitingFrequencyInputValue) {
                const coefficientServiceFrequency = parseBig(visitingFrequencyInputValue.monthlyService);
                if (new Big(article.totalQuantity).gt(new Big(0))
                    && parseBig(article.articleDetails["machineServiceTimeInSeconds"]).gt(new Big(0))
                    && parseBig(referential.tauxHoraire.value).gt(new Big(0))) {
                    return new Big(article.totalQuantity)
                        .mul(parseBig(article.articleDetails["machineServiceTimeInSeconds"]).div(parseBig("60")))
                        .mul(parseBig(referential.tauxHoraire.value))
                        .mul(coefficientServiceFrequency);
                } else {
                    return new Big("0.00");
                }
            }
        }
        return new Big("0.00");
    }

    public calculateUnitaryMepPrice(article: Article, contractDuration: Big, referential: any): Big {
        const vehicleType = referential.vehicle.filter((vehicle: any) => vehicle.type === 'lightVehicle')[0];
        const pricePerStop = parseBig(vehicleType.stopCost);
        if (new Big(article.totalQuantity).gt(new Big(0))
            && parseBig(article.articleDetails["machineInstallationTimeInSeconds"]).gt(new Big(0))
            && parseBig(referential.tauxHoraire.value).gt(new Big(0))) {
            return (pricePerStop.add((new Big(article.totalQuantity)
                .mul(parseBig(article.articleDetails["machineInstallationTimeInSeconds"]).div(parseBig("60")))
                .mul(parseBig(referential.tauxHoraire.value))))).div(contractDuration);
        } else {
            return parseBig("0");
        }
    }

    public updateLastCorridorStateBymultiplyingPrice(article: Article, factorImpact: Big): void {
        if (article.lastCorridorState) {
            this.corridorService.multiply(article.lastCorridorState, factorImpact);
        }
    }

    public getNewAppliedPriceWithDistance(matchedLevel: CorridorLevel, distance: string | Big.Big) {
        return new Big(matchedLevel.max).minus((new Big(matchedLevel.max).minus(new Big(matchedLevel.min))).mul(new Big(distance)));
    }

    private getDistanceFromAppliedPrice(article: Article) {
        if (article.lastCorridorState && article.appliedPrice) {
            return this.corridorService.getDistanceByAppliedPrice(article.lastCorridorState, article.appliedPrice);
        }
        return new Big(0);
    }

    public applyServiceGlobalDiscountToArticle(article: Article, distance: string, corridorLevel: CorridorLevel, serviceColor: string): void {
        if (article.appliedPrice && article.corridor) {
            let newAppliedPriceArticle: string;
            let newColor: string;
            if (distance && !this.isFlat(article)) {
                newAppliedPriceArticle = bigToString(this.getNewAppliedPriceWithDistance(corridorLevel, distance));
                newColor = this.corridorService.getMatchedColorByappliedPriceForNotFlatCorridor(article.corridor, newAppliedPriceArticle);
            } else {
                newColor = article.corridor.isNeutral ? EnumCorridorColor.GRAY : article.appliedPriceColor;
                newAppliedPriceArticle = article.appliedPrice;
            }
            const highestLevel = this.corridorService.getHighestLevel(article.corridor);
            const discount = Tools.getDiscount(parseBig(newAppliedPriceArticle), parseBig(highestLevel.min));
            const calculatedDistance = this.corridorService.getDistanceByAppliedPrice(article.corridor, newAppliedPriceArticle);
            Object.assign(article, {
                appliedPrice: newAppliedPriceArticle,
                discount: bigToString(discount),
                distance: bigToString(calculatedDistance),
                appliedPriceColor: newColor
            });
        } else {
            Object.assign(article, { discount: '0' });
        }
    }

    public getCorridor(article: Article, isWithQty: boolean, isWithNonNegotiableArticle: boolean, quantity: string): Corridor {
        if (isWithNonNegotiableArticle || (!isWithNonNegotiableArticle && !this.isFlat(article))) {
            if (isWithQty) {
                return article.corridor ? this.corridorService.multiplyQuantity(article.corridor, parseBig(quantity)) : undefined;
            }
            return article.corridor;
        }
        return new Corridor();
    }

    public addCorridor(article: Article, sumCorridorTobeUpdated: Corridor, isWithQty: boolean, isWithNonNegotiableArticle: boolean, quantity: string): void {
        if (isWithNonNegotiableArticle || (!isWithNonNegotiableArticle && !this.isFlat(article))) {
            if (isWithQty) {
                if (sumCorridorTobeUpdated && article.corridor) {
                    this.corridorService.addCorridor(sumCorridorTobeUpdated, this.corridorService.multiplyQuantity(article.corridor, parseBig(quantity)));
                }
            } else {
                if (sumCorridorTobeUpdated) {
                    this.corridorService.addCorridor(sumCorridorTobeUpdated, article.corridor);
                }
            }
        }
    }

    public sumAppliedPrices(article: Article, totaleAppliedPrice: Big, quantity: Big): Big {
        return totaleAppliedPrice.add(parseBig(article.appliedPrice).mul(quantity));
    }

    public isFlat(article: Article): boolean {
        return !!article.corridor ? this.corridorService.isFlat(article.corridor) : false;
    }

    public updateLastCorridorStateBymultiplyingSahpeSurfec(article: Article, shapeSurface: Big): void {
        if (article.lastCorridorState) {
            this.corridorService.multiply(article.lastCorridorState, shapeSurface);
        }
    }

    public isCustomCarpet(article: Article): Boolean {
        const articleDetails = article.articleDetails;
        if (articleDetails) {
            const subFamily = articleDetails['subFamily'];
            const family = articleDetails['family'];
            return subFamily === 'Dimension spécifique' && family === 'Tapis Personnalisé';
        }
        return false;
    }

    public updateLastCorridorStateBymultiplyingWaterDeliveryFactor(article: Article, waterDeliveryFactor: Big, isWaterBottleDeliveryWasChanged: boolean): void {
        if (!!article.articleDetails && article.articleDetails['subFamily'] === 'Bonbonne' && article.articleDetails['family'] === 'Consommable' && this.isLastCorridorState(article) && !!article.appliedPrice) {
            this.corridorService.multiply(article.lastCorridorState, waterDeliveryFactor);
            Object.assign(article, { appliedPriceColor: this.corridorService.getMatchedColorByappliedPriceForNotFlatCorridor(article.lastCorridorState, article.appliedPrice) });
        }
    }

    public updateLastAppliedPriceState(article: Article): void {
        if (!article.isRenewal || (article.appliedPrice && new Big(article.appliedPrice).gte(new Big(0)))) {
            Object.assign(article, { oldAppliedPrice: article.appliedPrice });
        }
    }

    public calculateExtraPrices(article: Article, serviceParameters: any, coefFs: Big, consumableExtraPrice: string, oldConsumableExtraPrice: string): any {
        const isNullOrUndefined = (value: any) => value == null || value == undefined;
        const isEqual = consumableExtraPrice === oldConsumableExtraPrice;
        if (isNullOrUndefined(article.appliedPrice)) {
            return { consumableExtraPrice: null, oldConsumableExtraPrice: null };
        }
        const calculate = (): string => {
            const extraPriceCoefficient = serviceParameters?.extraPriceCoefficient;
            return parseBig(article.appliedPrice).mul(coefFs).mul(parseBig("1").add(parseBig(extraPriceCoefficient))).toFixed(3);
        };
        const calculated = calculate();
        const shouldUpdate = !isNullOrUndefined(consumableExtraPrice) && !isNullOrUndefined(oldConsumableExtraPrice) && !isEqual;
        const isDuplication = !isNullOrUndefined(consumableExtraPrice) && isNullOrUndefined(oldConsumableExtraPrice) && !isEqual;
        if (shouldUpdate) {
            if (calculated !== oldConsumableExtraPrice) {
                return { consumableExtraPrice: calculated, oldConsumableExtraPrice: calculated };
            }
            return { consumableExtraPrice, oldConsumableExtraPrice };
        }
        if (isDuplication) {
            return { consumableExtraPrice, oldConsumableExtraPrice: calculated };
        }
        if (isEqual) {
            return { consumableExtraPrice: calculated, oldConsumableExtraPrice: calculated };
        }
        return { consumableExtraPrice, oldConsumableExtraPrice };
    }

    public calculateCollectorPrice(article: Article, serviceParameters: any, coefFs: Big, collectorExtraPrice: string, oldCollectorExtraPrice: string): any {
        const isNullOrUndefined = (value: any) => value == null || value == undefined;
        const isEqual = collectorExtraPrice === oldCollectorExtraPrice;
        if (isNullOrUndefined(article.appliedPrice)) {
            return { collectorExtraPrice: null, oldCollectorExtraPrice: null };
        }
        const calculate = (): string => {
            const coefficient = serviceParameters.collectorPriceCoefficient;
            return parseBig(article.appliedPrice).mul(coefFs).mul(parseBig("1").add(parseBig(coefficient))).toFixed(3);
        };
        const calculated = calculate();
        const shouldUpdate = !isNullOrUndefined(collectorExtraPrice) && !isNullOrUndefined(oldCollectorExtraPrice) && !isEqual;
        const isDuplication = !isNullOrUndefined(collectorExtraPrice) && isNullOrUndefined(oldCollectorExtraPrice) && !isEqual;
        if (shouldUpdate || isDuplication) {
            if (calculated !== oldCollectorExtraPrice) {
                return { collectorExtraPrice: calculated, oldCollectorExtraPrice: calculated };
            }
            return { collectorExtraPrice, oldCollectorExtraPrice };
        }
        if (isEqual) {
            return { collectorExtraPrice: calculated, oldCollectorExtraPrice: calculated };
        }
        return { collectorExtraPrice, oldCollectorExtraPrice };
    }

    public calculateDistance(article: Article, nonNegociableArticleColor: string): void {
        let distance = new Big(1);
        let matchedColor = null;
        if (!!article.lastCorridorState && !!article.corridor) {
            distance = this.corridorService.getDistanceByAppliedPrice(article.lastCorridorState, article.appliedPrice);
            matchedColor = this.corridorService.getMatchedColorByappliedPrice(article.lastCorridorState, article.appliedPrice, nonNegociableArticleColor);
            Object.assign(article, {
                distance: bigToString(distance),
                lastAppliedPriceColor: matchedColor,
                appliedPriceColor: matchedColor
            });
        } else if (!!article.distance) {
            Object.assign(article, { distance: article.distance, appliedPriceColor: article.lastAppliedPriceColor });
        } else if (article.appliedPrice == null || article.appliedPrice == undefined) {
            Object.assign(article, { distance: bigToString(distance), appliedPriceColor: matchedColor });
        }
    }

    public calculateDiscount(article: Article): void {
        Object.assign(article, { discount: "0" });
        if (!!article.corridor) {
            const highestLevel = this.corridorService.getHighestLevel(article.corridor);
            const discount = Tools.getDiscount(parseBig(article.appliedPrice), parseBig(highestLevel.min));
            Object.assign(article, { discount: bigToString(discount) });
        }
    }

    public isLastCorridorState(article: Article): boolean {
        return !!article.lastCorridorState;
    }

    public getNeutralCorridor(article: Article, userInputs: any, consoType?: string): Corridor {
        const appliedPrice = this.getArticleAppliedPrice(article, userInputs, consoType);
        let levels = null;
        if (appliedPrice) {
            levels = new Map<EnumCorridorColor, CorridorLevel>();
            levels.set(EnumCorridorColor.GREEN, new CorridorLevel(appliedPrice, appliedPrice));
            levels.set(EnumCorridorColor.LIGHTGREEN, new CorridorLevel(appliedPrice, appliedPrice));
            levels.set(EnumCorridorColor.ORANGE, new CorridorLevel(appliedPrice, appliedPrice));
            levels.set(EnumCorridorColor.RED, new CorridorLevel(appliedPrice, appliedPrice));
            levels.set(EnumCorridorColor.BLACK, new CorridorLevel(appliedPrice, appliedPrice));
            levels.set(EnumCorridorColor.BELOW_BLACK, new CorridorLevel(appliedPrice, "0"));
            return Object.assign(new Corridor(), { levels: levels, isNeutral: true });
        }
        return null;
    }

    private getArticleAppliedPrice(article: Article, userInputs: any, consoType?: string) {
        const appliedPrice = article.appliedPrice ? article.appliedPrice : article.oldAppliedPrice;
        return parseInt(userInputs.quantity) > 0 || consoType == 'VARIABLE' ? appliedPrice : null;
    }

    public updateDistanceAndAppliedPriceColor(article: Article, distance: string, matchedColor: string) {
        Object.assign(article, { distance: distance, appliedPriceColor: matchedColor });
    }

    public calculateArticleDistanceAndColorAndPriceFixedCorridorMode(article: Article, nonNegociableArticleColor: string) {
        const isGrayColor = (color: EnumCorridorColor): boolean => color == null || color == undefined || color === EnumCorridorColor.GRAY;
        if (article.oldDistance == null && isGrayColor(article.oldAppliedPriceColor) && !!article.appliedPrice && !isGrayColor(article.appliedPriceColor) && !!article.lastCorridorState) {
            if (this.corridorService.isFlat(article.lastCorridorState)) {
                const distance = "1";
                const matchedLevel = this.corridorService.getMatchedLevelByColor(article.lastCorridorState, nonNegociableArticleColor);
                let appliedPrice = null;
                if (matchedLevel) {
                    appliedPrice = bigToString(this.getNewAppliedPriceWithDistance(matchedLevel, distance));
                }
                Object.assign(article, {
                    appliedPrice,
                    distance,
                    appliedPriceColor: nonNegociableArticleColor,
                    oldAppliedPrice: appliedPrice,
                    oldDistance: distance,
                    oldAppliedPriceColor: nonNegociableArticleColor
                });
            } else {
                const color = this.corridorService.getMatchedColorByappliedPrice(article.lastCorridorState, article.appliedPrice, nonNegociableArticleColor);
                const distance = bigToString(this.corridorService.getDistanceByAppliedPrice(article.lastCorridorState, article.appliedPrice));
                Object.assign(article, {
                    appliedPrice: article.appliedPrice,
                    distance,
                    appliedPriceColor: color,
                    oldAppliedPrice: article.appliedPrice,
                    oldDistance: distance,
                    oldAppliedPriceColor: color
                });
            }
            return;
        }
        if (article.oldDistance == null && isGrayColor(article.oldAppliedPriceColor) && article.appliedPrice == null && !!article.lastCorridorState) {
            const color = this.corridorService.isFlat(article.lastCorridorState) ? nonNegociableArticleColor : EnumCorridorColor.GREEN;
            const distance = "1";
            const matchedLevel = this.corridorService.getMatchedLevelByColor(article.lastCorridorState, color);
            let appliedPrice = null;
            if (matchedLevel) {
                appliedPrice = bigToString(this.getNewAppliedPriceWithDistance(matchedLevel, distance));
            }
            Object.assign(article, {
                appliedPrice,
                distance,
                appliedPriceColor: color,
                oldAppliedPrice: appliedPrice,
                oldDistance: distance,
                oldAppliedPriceColor: color
            });
            return;
        }
        if (!!article.oldDistance && !isGrayColor(article.oldAppliedPriceColor) && article.appliedPrice == article.oldAppliedPrice && !!article.lastCorridorState) {
            const distance = article.oldDistance;
            const color = article.oldAppliedPriceColor;
            const matchedLevel = this.corridorService.getMatchedLevelByColor(article.lastCorridorState, color);
            let appliedPrice = null;
            if (matchedLevel) {
                appliedPrice = bigToString(this.getNewAppliedPriceWithDistance(matchedLevel, distance));
            }
            Object.assign(article, {
                appliedPrice,
                distance,
                appliedPriceColor: color,
                oldAppliedPrice: appliedPrice,
                oldDistance: distance,
                oldAppliedPriceColor: color
            });
            return;
        }
        if (!!article.oldDistance && !isGrayColor(article.oldAppliedPriceColor) && !!article.appliedPrice && article.appliedPrice !== article.oldAppliedPrice && !!article.lastCorridorState) {
            const color = this.corridorService.getMatchedColorByappliedPrice(article.lastCorridorState, article.appliedPrice, nonNegociableArticleColor);
            const distance = bigToString(this.corridorService.getDistanceByAppliedPrice(article.lastCorridorState, article.appliedPrice));
            Object.assign(article, {
                appliedPrice: article.appliedPrice,
                distance,
                appliedPriceColor: color,
                oldAppliedPrice: article.appliedPrice,
                oldDistance: distance,
                oldAppliedPriceColor: color
            });
            return;
        }
        if (!!article.oldDistance && !isGrayColor(article.oldAppliedPriceColor) && article.lastCorridorState == null) {
            Object.assign(article, {
                appliedPrice: null,
                distance: null,
                appliedPriceColor: EnumCorridorColor.GRAY,
                oldAppliedPrice: null
            });
            return;
        }
        Object.assign(article, {
            appliedPrice: null,
            distance: null,
            appliedPriceColor: EnumCorridorColor.GRAY,
            oldAppliedPrice: null,
            oldDistance: null,
            oldAppliedPriceColor: EnumCorridorColor.GRAY
        });
    }

    public calculateArticleDistanceAndColorAndPriceFixedPriceMode(article: Article, nonNegociableArticleColor: string) {
        if (article.lastCorridorState && !article.lastCorridorState.isNeutral) {
            if (article.appliedPrice) {
                const color = this.corridorService.getMatchedColorByappliedPrice(article.lastCorridorState, article.appliedPrice, nonNegociableArticleColor);
                const distance = bigToString(this.corridorService.getDistanceByAppliedPrice(article.lastCorridorState, article.appliedPrice));
                Object.assign(article, {
                    distance,
                    appliedPriceColor: color,
                    oldAppliedPrice: article.appliedPrice,
                    oldDistance: distance,
                    oldAppliedPriceColor: color
                });
                return;
            }
            if (article.oldAppliedPrice) {
                Object.assign(article, {
                    distance: article.oldDistance,
                    appliedPriceColor: article.oldAppliedPriceColor,
                    appliedPrice: article.oldAppliedPrice
                });
                return;
            }
        }
        if (article.lastCorridorState && article.lastCorridorState.isNeutral) {
            if (article.appliedPrice) {
                Object.assign(article, {
                    distance: "1",
                    appliedPriceColor: EnumCorridorColor.GRAY,
                    oldAppliedPrice: article.appliedPrice,
                    oldDistance: "1",
                    oldAppliedPriceColor: EnumCorridorColor.GRAY
                });
                return;
            }
            if (article.oldAppliedPrice) {
                Object.assign(article, {
                    distance: article.oldDistance,
                    appliedPriceColor: article.oldAppliedPriceColor,
                    appliedPrice: article.oldAppliedPrice
                });
                return;
            }
        }
        if (!article.lastCorridorState) {
            Object.assign(article, { distance: null, appliedPriceColor: EnumCorridorColor.GRAY, appliedPrice: null });
            return;
        }
    }

    public setArticleCorridorFromArticleLastCorridorState(article: Article) {
        Object.assign(article, { corridor: article.lastCorridorState });
    }

    public updateLastCorridorStateByMultiplyingImpactSuppOffer(article: Article, impactSuppOffer: number) {
        if (this.isLastCorridorState(article) && !!article.appliedPrice) {
            this.updateLastCorridorStateBymultiplyingPrice(article, new Big(1 + impactSuppOffer));
            Object.assign(article, { appliedPriceColor: this.corridorService.getMatchedColorByappliedPriceForNotFlatCorridor(article.lastCorridorState, article.appliedPrice) });
        }
    }

    public applyFreeMonthFactor(article: Article, factorImpact: Big) {
        if (this.isLastCorridorState(article) && !!article.appliedPrice) {
            this.updateLastCorridorStateBymultiplyingPrice(article, factorImpact);
            Object.assign(article, { appliedPriceColor: this.corridorService.getMatchedColorByappliedPriceForNotFlatCorridor(article.lastCorridorState, article.appliedPrice) });
        }
    }

    public applyContractVtFactor(article: Article, factorImpact: Big) {
        if (this.isLastCorridorState(article)) {
            this.updateLastCorridorStateBymultiplyingPrice(article, factorImpact);
        }
    }

    public copyArticleAppliedPriceToOldAppliedPrice(article: Article) {
        if (!!article.distance && !!article.appliedPriceColor && !!article.appliedPrice) {
            Object.assign(article, {
                oldAppliedPrice: article.appliedPrice,
                oldAppliedPriceColor: article.appliedPriceColor,
                oldDistance: article.distance
            });
        }
    }
}
